import React from 'react';
import { motion } from 'framer-motion';
import { MailCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  email,
}) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-sm rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700/50 p-6 flex flex-col gap-6 text-center"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col gap-1 items-center">
          <MailCheck className="size-10 text-primary mb-3" />
          <h2 className="text-xl font-bold text-[#181411] dark:text-white">{t('auth.verification_modal_title')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {t('auth.verification_modal_message', { email })}
          </p>
        </div>

        {/* Footer / Actions */}
        <div className="flex justify-center pt-2">
          <motion.button
            type="button"
            onClick={onClose}
            className="flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:opacity-90 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('auth.verification_modal_button')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EmailVerificationModal;